import store from '@/store/index';
import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule,
} from 'vuex-module-decorators';

import i18n from '@/plugins/i18n';
import BiPanelRuleService from '@/services/bi-panel-rule-service';
import {
  BiPanelFilterFieldValueAction,
  BiPanelFilterOptionsSearchAction,
  BiPanelHistoryDisplayEntry,
  BiPanelHistoryEntry,
  BiPanelParameterCreatePayload,
  BiPanelParameterUpdatePayload,
  BiPanelRule,
  BiPanelRuleActionResult,
  BiPanelRuleCreatePayload,
  BiPanelRuleFilter,
  BiPanelRuleFilterField,
  BiPanelRuleFilterOptionField,
  BiPanelRuleFormParameter,
  BiPanelRuleModel,
  BiPanelRuleTableRow,
  BiParameterTypeCode,
  BISelectOption,
  BiPanelLevelCode,
  PaymentModelCode,
} from './bi-panel-rule-types';
import {
  BI_COMPETENCY_TYPE_OPTIONS,
  BI_LIFE_COUNT_RULE_OPTIONS,
  BI_PANEL_LEVEL_OPTIONS,
  BI_PANEL_RULE_MAX_PARAMETERS_PER_RULE,
  BI_PARAMETER_TYPE_OPTIONS,
  BI_PAYMENT_MODEL_OPTIONS,
  defaultBiPanelRuleModel,
  defaultCompetencyType,
  defaultFilter,
  defaultFilterOptionFieldState,
  defaultFilterOptions,
  defaultFormState,
} from './bi-panel-rule-factory';

const biPanelRuleService = new BiPanelRuleService();

const FILTER_FIELDS_TO_CLEAR: Partial<Record<BiPanelRuleFilterField, BiPanelRuleFilterField[]>> = { financialGroupId: ['contractIds'] };

const FILTER_OPTION_FIELDS_TO_RESET: Partial<Record<BiPanelRuleFilterField, BiPanelRuleFilterOptionField[]>> = { financialGroupId: ['contract'] };

let formParameterSequence = 0;
const nextFormParameterTempId = (): number => {
  formParameterSequence += 1;
  return formParameterSequence;
};

type RuleScopeField = 'carrierId' | 'financialGroupId' | 'contractId';

const SCOPE_FIELD_BY_LEVEL: Record<BiPanelLevelCode, RuleScopeField> = {
  CARRIER: 'carrierId',
  FINANCIAL_GROUP: 'financialGroupId',
  CONTRACT: 'contractId',
};

const LEVEL_BY_SCOPE_FIELD: Record<RuleScopeField, BiPanelLevelCode> = {
  carrierId: 'CARRIER',
  financialGroupId: 'FINANCIAL_GROUP',
  contractId: 'CONTRACT',
};

const getRuleScopeId = (rule: BiPanelRule): number | null => rule[SCOPE_FIELD_BY_LEVEL[rule.level.code]] ?? null;

// O histórico (Envers) devolve códigos crus; mapeia código de enum -> label de exibição
const ENUM_LABEL_BY_CODE: Record<string, string> = [
  ...BI_PANEL_LEVEL_OPTIONS,
  ...BI_PAYMENT_MODEL_OPTIONS,
  ...BI_PARAMETER_TYPE_OPTIONS,
  ...BI_LIFE_COUNT_RULE_OPTIONS,
  ...BI_COMPETENCY_TYPE_OPTIONS,
].reduce((acc: Record<string, string>, option) => {
  acc[option.code] = option.label;
  return acc;
}, {});

const ENUM_CODE_PATTERN = new RegExp(`\\b(${Object.keys(ENUM_LABEL_BY_CODE).join('|')})\\b`, 'g');

const translateOrKeep = (key: string, fallback: string): string => {
  const translated = i18n.t(key).toString();

  return translated === key ? fallback : translated;
};

// Helpers fora da classe: no vuex-module-decorators o `this` de uma @Action só expõe state, getters,
// mutations e actions — métodos comuns da classe não são acessíveis ali
const flattenRules = (rules: BiPanelRule[]): BiPanelRuleTableRow[] => rules.flatMap(
  (rule) => (rule.parameters || []).map((parameter) => ({
    id: parameter.id,
    ruleId: rule.id,
    ruleActive: rule.active,
    level: rule.level,
    levelOptionId: getRuleScopeId(rule),
    levelOptionLabel: '',
    paymentModels: rule.paymentModels,
    parameterType: parameter.parameterType,
    parameterValue: parameter.lifeCountRule ?? parameter.competencyType ?? null,
    active: parameter.active,
    updatedAt: parameter.updatedAt,
    updatedBy: parameter.updatedBy,
  })),
);

const formatHistoryValue = (
  attributeName: string,
  value: string | null,
  levelOptionNames: Record<string, string>,
): string | null => {
  if (value === null || value === undefined) return value;

  if (value === 'true' || value === 'false') {
    return i18n.t(`biControlPanel.parameterRules.table.${value === 'true' ? 'active' : 'inactive'}`).toString();
  }

  const scopeLevel = LEVEL_BY_SCOPE_FIELD[attributeName as RuleScopeField];

  if (scopeLevel) {
    return levelOptionNames[`${scopeLevel}:${value}`] || value;
  }

  // Cobre valor único ("PC") e listas serializadas ("[PRE_PAGAMENTO, POS_PAGAMENTO]")
  return value.replace(ENUM_CODE_PATTERN, (code) => ENUM_LABEL_BY_CODE[code] || code);
};

const formatHistoryEntry = (
  entry: BiPanelHistoryEntry,
  row: BiPanelRuleTableRow,
  levelOptionNames: Record<string, string>,
): BiPanelHistoryDisplayEntry => ({
  ...entry,
  levelLabel: [row.level.label, row.levelOptionLabel].filter(Boolean).join(' - '),
  revisions: entry.revisions.map((revision) => ({
    ...revision,
    revType: translateOrKeep(`biControlPanel.parameterRules.history.revTypes.${revision.revType}`, revision.revType),
    changes: revision.changes.map((change) => ({
      attributeName: translateOrKeep(
        `biControlPanel.parameterRules.history.attributes.${change.attributeName}`,
        change.attributeName,
      ),
      oldValue: formatHistoryValue(change.attributeName, change.oldValue, levelOptionNames),
      newValue: formatHistoryValue(change.attributeName, change.newValue, levelOptionNames),
    })),
  })),
});

const extractErrorMessage = (error: unknown, fallback: string): { message: string; code?: string } => {
  const axiosError = error as {
    message?: string;
    response?: {
      data?: {
        message?: string;
        error?: string;
        codeMessage?: string;
      } | string;
    };
  };
  const responseData = axiosError?.response?.data;

  if (typeof responseData === 'string' && responseData.trim()) {
    return { message: responseData };
  }

  if (typeof responseData === 'object' && responseData) {
    if (typeof responseData.message === 'string' && responseData.message.trim()) {
      return { message: responseData.message, code: responseData.codeMessage };
    }

    if (typeof responseData.error === 'string' && responseData.error.trim()) {
      return { message: responseData.error, code: responseData.codeMessage };
    }
  }

  if (typeof axiosError?.message === 'string' && axiosError.message.trim()) {
    return { message: axiosError.message };
  }

  return { message: fallback };
};

@Module({
  namespaced: true,
  name: 'bi-panel-rule',
  dynamic: true,
  store,
})
class BiPanelRuleStore extends VuexModule {
  data: BiPanelRuleModel = defaultBiPanelRuleModel();

  get filter() {
    return this.data.filter;
  }

  get filterOptions() {
    return this.data.filterOptions;
  }

  get table() {
    return this.data.table;
  }

  get form() {
    return this.data.form;
  }

  get history() {
    return this.data.history;
  }

  get canAddFormParameter() {
    const { parameters } = this.data.form;

    if (parameters.length >= BI_PANEL_RULE_MAX_PARAMETERS_PER_RULE) return false;

    const usedTypes = new Set(parameters.map((parameter) => parameter.parameterType).filter(Boolean));

    return usedTypes.size < BI_PANEL_RULE_MAX_PARAMETERS_PER_RULE;
  }

  @Action
  async initializeTab(): Promise<BiPanelRuleActionResult> {
    // Os filtros sobrevivem à navegação; ao voltar do formulário a busca é refeita para refletir o que foi salvo
    if (this.data.initialized) {
      return this.searchTable();
    }

    const result = await this.searchTable();

    if (result.success) {
      this.SET_INITIALIZED(true);
    }

    return result;
  }

  @Action
  async clearFilters(): Promise<BiPanelRuleActionResult> {
    this.RESET_FILTER();
    this.RESET_FILTER_OPTIONS();
    this.SET_TABLE_PAGE(0);

    return this.searchTable();
  }

  @Action
  async submitFilters(): Promise<BiPanelRuleActionResult> {
    this.SET_TABLE_PAGE(0);

    return this.searchTable();
  }

  @Action
  setFilterValue(payload: BiPanelFilterFieldValueAction) {
    const previousValue = this.data.filter[payload.fieldKey];
    const changed = JSON.stringify(previousValue) !== JSON.stringify(payload.value);

    this.SET_FILTER({ [payload.fieldKey]: payload.value } as Partial<BiPanelRuleFilter>);

    if (!changed) return;

    this.APPLY_FILTER_DEPENDENCIES(payload.fieldKey);
  }

  @Action
  async changeTablePage(page: number): Promise<BiPanelRuleActionResult> {
    const { totalPages, page: currentPage } = this.data.table.pagination;
    const normalizedPage = Math.max(0, page);
    const outOfBounds = totalPages > 0 && normalizedPage >= totalPages;

    if (currentPage === normalizedPage || outOfBounds) {
      return { success: true };
    }

    this.SET_TABLE_PAGE(normalizedPage);

    return this.searchTable();
  }

  @Action
  async changeTableSize(size: number): Promise<BiPanelRuleActionResult> {
    const normalizedSize = Number(size);

    if (!normalizedSize || normalizedSize === this.data.table.pagination.size) {
      return { success: true };
    }

    this.SET_TABLE_SIZE(normalizedSize);
    this.SET_TABLE_PAGE(0);

    return this.searchTable();
  }

  @Action
  async searchTable(): Promise<BiPanelRuleActionResult> {
    try {
      this.SET_TABLE_LOADING(true);

      const { page, size } = this.data.table.pagination;
      const result = await biPanelRuleService.fetchRules({
        page,
        size,
        filter: this.data.filter,
      });
      const rows = flattenRules(result.content);

      this.SET_TABLE_ROWS(rows);
      this.SET_TABLE_PAGINATION({
        page: result.page,
        size: result.size,
        totalElements: result.totalElements,
        totalPages: result.totalPages,
      });

      await this.resolveLevelOptionNames(rows);

      return { success: true };
    } catch (error) {
      const { message } = extractErrorMessage(error, 'Ocorreu um erro ao buscar os dados da tabela.');

      return { success: false, errorMessage: message };
    } finally {
      this.SET_TABLE_LOADING(false);
    }
  }

  @Action
  async searchFilterOptions(
    payload: BiPanelFilterOptionsSearchAction,
  ): Promise<BiPanelRuleActionResult<{ content: BISelectOption[]; last: boolean }>> {
    const { fieldKey, reset = true } = payload;
    const fieldState = this.data.filterOptions[fieldKey];

    if (fieldState.loading && typeof payload.page !== 'number') {
      return { success: true, data: { content: fieldState.items, last: !fieldState.pagination.hasNext } };
    }

    if (!reset && !fieldState.pagination.hasNext) {
      return { success: true, data: { content: fieldState.items, last: true } };
    }

    const searchText = typeof payload.searchText === 'string' ? payload.searchText : fieldState.searchInput;
    const page = typeof payload.page === 'number'
      ? payload.page
      : reset ? 0 : fieldState.pagination.page + 1;
    const size = typeof payload.size === 'number' ? payload.size : fieldState.pagination.size;

    this.SET_FILTER_OPTIONS_LOADING({ fieldKey, loading: true });
    this.SET_FILTER_OPTIONS_SEARCH_INPUT({ fieldKey, value: searchText });

    try {
      let response;

      switch (fieldKey) {
        case 'financialGroup':
          response = await biPanelRuleService.fetchFinancialGroupOptions(page, size, searchText);
          break;
        case 'carrier':
          response = await biPanelRuleService.fetchCarrierOptions(page, size, searchText);
          break;
        case 'contract':
          response = await biPanelRuleService.fetchContractOptions(
            page,
            size,
            searchText,
            this.data.filter.financialGroupId,
          );
          break;
        default:
          throw new Error(`Unsupported BI panel rule filter option field: ${fieldKey}`);
      }

      const { content, last } = response.data;

      this.SET_FILTER_OPTIONS_ITEMS({ fieldKey, items: content, reset });
      this.SET_FILTER_OPTIONS_PAGINATION({
        fieldKey,
        pagination: { page, size, totalElements: content.length, hasNext: !last },
      });
      this.SET_FILTER_OPTIONS_INITIALIZED({ fieldKey, initialized: true });

      return { success: true, data: { content, last } };
    } catch (error) {
      const { message } = extractErrorMessage(error, 'Ocorreu um erro ao buscar as opções do filtro.');

      return { success: false, errorMessage: message };
    } finally {
      this.SET_FILTER_OPTIONS_LOADING({ fieldKey, loading: false });
    }
  }

  @Action
  async updateParameterStatus(payload: { id: number; active: boolean }): Promise<BiPanelRuleActionResult> {
    try {
      await biPanelRuleService.updateParameterStatus(payload.id, payload.active);
      this.SET_ROW_PARAMETER_STATUS(payload);

      return { success: true };
    } catch (error) {
      const { message } = extractErrorMessage(error, 'Ocorreu um erro ao atualizar o status do parâmetro.');

      return { success: false, errorMessage: message };
    }
  }

  @Action
  async updateRuleStatus(payload: { ruleId: number; active: boolean }): Promise<BiPanelRuleActionResult> {
    try {
      await biPanelRuleService.updateRuleStatus(payload.ruleId, payload.active);
      this.SET_ROW_RULE_STATUS(payload);

      return { success: true };
    } catch (error) {
      const { message } = extractErrorMessage(error, 'Ocorreu um erro ao atualizar o status da regra.');

      return { success: false, errorMessage: message };
    }
  }

  @Action
  async fetchRuleHistory(row: BiPanelRuleTableRow): Promise<BiPanelRuleActionResult> {
    try {
      this.SET_HISTORY_LOADING(true);
      const entry = await biPanelRuleService.fetchRuleHistory(row.ruleId);

      this.SET_RULE_HISTORY([formatHistoryEntry(entry, row, this.data.levelOptionNames)]);

      return { success: true };
    } catch (error) {
      const { message } = extractErrorMessage(error, 'Ocorreu um erro ao buscar o histórico da regra.');

      return { success: false, errorMessage: message };
    } finally {
      this.SET_HISTORY_LOADING(false);
    }
  }

  @Action
  async fetchParameterHistory(row: BiPanelRuleTableRow): Promise<BiPanelRuleActionResult> {
    try {
      this.SET_HISTORY_LOADING(true);
      const entry = await biPanelRuleService.fetchParameterHistory(row.id);

      this.SET_PARAMETER_HISTORY([formatHistoryEntry(entry, row, this.data.levelOptionNames)]);

      return { success: true };
    } catch (error) {
      const { message } = extractErrorMessage(error, 'Ocorreu um erro ao buscar o histórico do parâmetro.');

      return { success: false, errorMessage: message };
    } finally {
      this.SET_HISTORY_LOADING(false);
    }
  }

  @Action
  openCreateForm() {
    this.RESET_FORM();
    this.SET_FORM_PATCH({ show: true, mode: 'create', step: 1 });
    this.ADD_FORM_PARAMETER();
  }

  @Action
  async openEditForm(ruleId: number): Promise<BiPanelRuleActionResult> {
    this.RESET_FORM();
    this.SET_FORM_PATCH({ show: true, mode: 'edit', step: 2, loading: true });

    try {
      const rule = await biPanelRuleService.fetchRule(ruleId);
      const scopeLabel = await this.resolveScopeLabel(rule);

      this.SET_FORM_FROM_RULE(rule);
      this.SET_FORM_PATCH({ scopeLabel });

      return { success: true };
    } catch (error) {
      const { message } = extractErrorMessage(error, 'Ocorreu um erro ao carregar a regra.');

      this.SET_FORM_PATCH({ show: false });

      return { success: false, errorMessage: message };
    } finally {
      this.SET_FORM_PATCH({ loading: false });
    }
  }

  @Action
  closeForm() {
    this.RESET_FORM();
  }

  @Action
  setFormLevel(level: BiPanelLevelCode | null) {
    this.SET_FORM_PATCH({
      level,
      carrierId: null,
      financialGroupId: null,
      contractId: null,
    });
  }

  @Action
  setFormScopeValue(payload: { field: 'carrierId' | 'financialGroupId' | 'contractId'; value: number | null }) {
    const patch: Partial<Record<string, number | null>> = { [payload.field]: payload.value };

    if (payload.field === 'financialGroupId') {
      patch.contractId = null;
    }

    this.SET_FORM_PATCH(patch as Partial<BiPanelRuleModel['form']>);
  }

  @Action
  setFormPaymentModels(paymentModels: PaymentModelCode[]) {
    const nextDefault = defaultCompetencyType(paymentModels);

    this.SET_FORM_PATCH({ paymentModels });

    if (!nextDefault) return;

    // Só reaplica o padrão onde o usuário não escolheu outro valor
    this.data.form.parameters
      .filter((parameter) => parameter.parameterType === 'COMPETENCY'
        && (!parameter.competencyType || parameter.competencyIsDefault))
      .forEach(({ tempId }) => this.UPDATE_FORM_PARAMETER({
        tempId,
        patch: { competencyType: nextDefault, competencyIsDefault: true },
      }));
  }

  @Action
  goToFormStep(step: 1 | 2) {
    this.SET_FORM_PATCH({ step });
  }

  @Action
  addFormParameter() {
    if (!this.canAddFormParameter) return;

    this.ADD_FORM_PARAMETER();
  }

  @Action
  removeFormParameter(tempId: number) {
    this.REMOVE_FORM_PARAMETER(tempId);
  }

  @Action
  updateFormParameter(payload: { tempId: number; patch: Partial<BiPanelRuleFormParameter> }) {
    this.UPDATE_FORM_PARAMETER(payload);
  }

  @Action
  async submitForm(): Promise<BiPanelRuleActionResult> {
    const { form } = this.data;

    let createdRule: BiPanelRule | null = null;

    this.SET_FORM_PATCH({ submitting: true });

    try {
      if (form.mode === 'create') {
        const payload: BiPanelRuleCreatePayload = {
          level: form.level as BiPanelLevelCode,
          carrierId: form.level === 'CARRIER' ? form.carrierId : null,
          // No nível CONTRACT o Grupo Financeiro serve só para a cascata do combo; o BE exige os demais escopos nulos
          financialGroupId: form.level === 'FINANCIAL_GROUP' ? form.financialGroupId : null,
          contractId: form.level === 'CONTRACT' ? form.contractId : null,
          paymentModels: form.paymentModels as BiPanelRuleCreatePayload['paymentModels'],
          parameters: form.parameters.map((parameter) => ({
            parameterType: parameter.parameterType as BiParameterTypeCode,
            lifeCountRule: parameter.lifeCountRule,
            competencyType: parameter.competencyType,
          })),
        };

        createdRule = await biPanelRuleService.createRule(payload);

        this.LINK_FORM_TO_CREATED_RULE(createdRule);

        await Promise.all(this.data.form.parameters.map((parameter) => this.applyParameterStatus(parameter)));
      } else {
        await this.submitEditedParameters();
      }

      this.RESET_FORM();

      return { success: true };
    } catch (error) {
      const { message, code } = extractErrorMessage(error, 'Ocorreu um erro ao salvar a regra.');

      if (createdRule) {
        this.SET_FORM_PATCH({ scopeLabel: await this.resolveScopeLabel(createdRule) });
      }

      // 409 na criação da regra (ainda sem createdRule) indica regra ativa duplicada para o nível+escopo
      // errorResponseInterceptor rejeita com o próprio response; sem interceptor o status vem em error.response
      const { status: interceptedStatus, response } = (error ?? {}) as { status?: number; response?: { status?: number } };
      const status = interceptedStatus ?? response?.status;

      return {
        success: false,
        errorMessage: message,
        errorCode: code,
        errorStatus: createdRule ? undefined : status,
      };
    } finally {
      this.SET_FORM_PATCH({ submitting: false });
    }
  }

  @Action
  private async submitEditedParameters(): Promise<void> {
    const { form } = this.data;

    if (!form.ruleId) return;

    const newParameters = form.parameters.filter((parameter) => parameter.isNew);
    const existingParameters = form.parameters.filter((parameter) => !parameter.isNew && parameter.id);

    await Promise.all(newParameters.map(async (parameter) => {
      const payload: BiPanelParameterCreatePayload = {
        parameterType: parameter.parameterType as BiParameterTypeCode,
        lifeCountRule: parameter.lifeCountRule,
        competencyType: parameter.competencyType,
      };

      const created = await biPanelRuleService.addParameter(form.ruleId as number, payload);
      const linked = { id: created.id, originalActive: created.active, isNew: false };

      this.UPDATE_FORM_PARAMETER({ tempId: parameter.tempId, patch: linked });

      await this.applyParameterStatus({ ...parameter, ...linked });
    }));

    await Promise.all(existingParameters.map(async (parameter) => {
      const payload: BiPanelParameterUpdatePayload = {
        lifeCountRule: parameter.lifeCountRule,
        competencyType: parameter.competencyType,
      };

      await biPanelRuleService.updateParameter(parameter.id as number, payload);
      await this.applyParameterStatus(parameter);
    }));
  }

  @Action
  private async applyParameterStatus(parameter: BiPanelRuleFormParameter): Promise<void> {
    if (!parameter.id || parameter.active === parameter.originalActive) return;

    await biPanelRuleService.updateParameterStatus(parameter.id, parameter.active);

    this.UPDATE_FORM_PARAMETER({ tempId: parameter.tempId, patch: { originalActive: parameter.active } });
  }

  @Action
  private async resolveScopeLabel(rule: BiPanelRule): Promise<string> {
    const scopeId = getRuleScopeId(rule);

    if (scopeId === null) return '';

    const cacheKey = `${rule.level.code}:${scopeId}`;
    const cached = this.data.levelOptionNames[cacheKey];

    if (cached !== undefined) return cached;

    try {
      const resolvers: Record<BiPanelLevelCode, (ids: number[]) => Promise<Record<number, string>>> = {
        CARRIER: (ids) => biPanelRuleService.resolveCarrierNames(ids),
        FINANCIAL_GROUP: (ids) => biPanelRuleService.resolveFinancialGroupNames(ids),
        CONTRACT: (ids) => biPanelRuleService.resolveContractNames(ids),
      };
      const names = await resolvers[rule.level.code]([scopeId]);

      this.MERGE_LEVEL_OPTION_NAMES({ level: rule.level.code, names });

      return names[scopeId] || String(scopeId);
    } catch {
      return String(scopeId);
    }
  }

  @Action
  private async resolveLevelOptionNames(rows: BiPanelRuleTableRow[]): Promise<void> {
    const idsByLevel: Record<BiPanelLevelCode, Set<number>> = {
      CARRIER: new Set(),
      FINANCIAL_GROUP: new Set(),
      CONTRACT: new Set(),
    };

    rows.forEach((row) => {
      if (row.levelOptionId == null) return;

      const cacheKey = `${row.level.code}:${row.levelOptionId}`;

      if (this.data.levelOptionNames[cacheKey] !== undefined) return;

      idsByLevel[row.level.code].add(row.levelOptionId);
    });

    const [carrierNames, financialGroupNames, contractNames] = await Promise.all([
      biPanelRuleService.resolveCarrierNames([...idsByLevel.CARRIER]),
      biPanelRuleService.resolveFinancialGroupNames([...idsByLevel.FINANCIAL_GROUP]),
      biPanelRuleService.resolveContractNames([...idsByLevel.CONTRACT]),
    ]);

    this.MERGE_LEVEL_OPTION_NAMES({ level: 'CARRIER', names: carrierNames });
    this.MERGE_LEVEL_OPTION_NAMES({ level: 'FINANCIAL_GROUP', names: financialGroupNames });
    this.MERGE_LEVEL_OPTION_NAMES({ level: 'CONTRACT', names: contractNames });
    this.APPLY_LEVEL_OPTION_LABELS();
  }

  @Mutation
  private SET_INITIALIZED(value: boolean) {
    this.data.initialized = value;
  }

  @Mutation
  private RESET_FILTER() {
    this.data.filter = defaultFilter();
  }

  @Mutation
  private SET_FILTER(patch: Partial<BiPanelRuleFilter>) {
    this.data.filter = { ...this.data.filter, ...patch };
  }

  @Mutation
  private RESET_FILTER_OPTIONS() {
    this.data.filterOptions = defaultFilterOptions();
  }

  @Mutation
  private APPLY_FILTER_DEPENDENCIES(fieldKey: BiPanelRuleFilterField) {
    const fieldsToClear = FILTER_FIELDS_TO_CLEAR[fieldKey] || [];
    const optionFieldsToReset = FILTER_OPTION_FIELDS_TO_RESET[fieldKey] || [];

    if (fieldsToClear.length) {
      fieldsToClear.forEach((field) => {
        (this.data.filter as unknown as Record<string, unknown>)[field] = Array.isArray(this.data.filter[field])
          ? []
          : null;
      });
    }

    optionFieldsToReset.forEach((field) => {
      this.data.filterOptions[field] = defaultFilterOptionFieldState();
    });
  }

  @Mutation
  private SET_TABLE_LOADING(loading: boolean) {
    this.data.table.loading = loading;
  }

  @Mutation
  private SET_TABLE_ROWS(rows: BiPanelRuleTableRow[]) {
    this.data.table.rows = rows;
  }

  @Mutation
  private SET_TABLE_PAGE(page: number) {
    this.data.table.pagination.page = page;
  }

  @Mutation
  private SET_TABLE_SIZE(size: number) {
    this.data.table.pagination.size = size;
  }

  @Mutation
  private SET_TABLE_PAGINATION(pagination: Partial<BiPanelRuleModel['table']['pagination']>) {
    this.data.table.pagination = { ...this.data.table.pagination, ...pagination };
  }

  @Mutation
  private SET_ROW_PARAMETER_STATUS(payload: { id: number; active: boolean }) {
    this.data.table.rows = this.data.table.rows.map((row) => (
      row.id === payload.id ? { ...row, active: payload.active } : row
    ));
  }

  @Mutation
  private SET_ROW_RULE_STATUS(payload: { ruleId: number; active: boolean }) {
    this.data.table.rows = this.data.table.rows.map((row) => (
      row.ruleId === payload.ruleId ? { ...row, ruleActive: payload.active, active: payload.active } : row
    ));
  }

  @Mutation
  private SET_FILTER_OPTIONS_LOADING(payload: { fieldKey: BiPanelRuleFilterOptionField; loading: boolean }) {
    this.data.filterOptions[payload.fieldKey].loading = payload.loading;
  }

  @Mutation
  private SET_FILTER_OPTIONS_SEARCH_INPUT(payload: { fieldKey: BiPanelRuleFilterOptionField; value: string }) {
    this.data.filterOptions[payload.fieldKey].searchInput = payload.value;
  }

  @Mutation
  private SET_FILTER_OPTIONS_INITIALIZED(payload: { fieldKey: BiPanelRuleFilterOptionField; initialized: boolean }) {
    this.data.filterOptions[payload.fieldKey].initialized = payload.initialized;
  }

  @Mutation
  private SET_FILTER_OPTIONS_ITEMS(payload: {
    fieldKey: BiPanelRuleFilterOptionField;
    items: BISelectOption[];
    reset: boolean;
  }) {
    const fieldState = this.data.filterOptions[payload.fieldKey];

    fieldState.items = payload.reset ? payload.items : [...fieldState.items, ...payload.items];
  }

  @Mutation
  private SET_FILTER_OPTIONS_PAGINATION(payload: {
    fieldKey: BiPanelRuleFilterOptionField;
    pagination: Partial<BiPanelRuleModel['filterOptions']['financialGroup']['pagination']>;
  }) {
    const fieldState = this.data.filterOptions[payload.fieldKey];

    fieldState.pagination = { ...fieldState.pagination, ...payload.pagination };
  }

  @Mutation
  private SET_HISTORY_LOADING(loading: boolean) {
    this.data.history.loading = loading;
  }

  @Mutation
  private SET_RULE_HISTORY(entries: BiPanelRuleModel['history']['rule']) {
    this.data.history.rule = entries;
  }

  @Mutation
  private SET_PARAMETER_HISTORY(entries: BiPanelRuleModel['history']['parameter']) {
    this.data.history.parameter = entries;
  }

  @Mutation
  private RESET_FORM() {
    this.data.form = defaultFormState();
  }

  @Mutation
  private SET_FORM_PATCH(patch: Partial<BiPanelRuleModel['form']>) {
    this.data.form = { ...this.data.form, ...patch };
  }

  @Mutation
  private SET_FORM_FROM_RULE(rule: BiPanelRule) {
    this.data.form = {
      ...this.data.form,
      ruleId: rule.id,
      level: rule.level.code,
      carrierId: rule.carrierId ?? null,
      financialGroupId: rule.financialGroupId ?? null,
      contractId: rule.contractId ?? null,
      paymentModels: rule.paymentModels.map((paymentModel) => paymentModel.code),
      parameters: (rule.parameters || []).map((parameter) => ({
        tempId: nextFormParameterTempId(),
        id: parameter.id,
        parameterType: parameter.parameterType.code,
        lifeCountRule: parameter.lifeCountRule?.code ?? null,
        competencyType: parameter.competencyType?.code ?? null,
        active: parameter.active,
        originalActive: parameter.active,
        isNew: false,
      })),
    };
  }

  @Mutation
  private LINK_FORM_TO_CREATED_RULE(rule: BiPanelRule) {
    this.data.form = {
      ...this.data.form,
      mode: 'edit',
      ruleId: rule.id,
      parameters: this.data.form.parameters.map((parameter) => {
        const created = (rule.parameters || []).find(
          (candidate) => candidate.parameterType.code === parameter.parameterType,
        );

        if (!created) return parameter;

        return { ...parameter, id: created.id, originalActive: created.active, isNew: false };
      }),
    };
  }

  @Mutation
  private ADD_FORM_PARAMETER() {
    this.data.form.parameters = [
      {
        tempId: nextFormParameterTempId(),
        id: null,
        parameterType: null,
        lifeCountRule: null,
        competencyType: null,
        active: true,
        originalActive: true,
        isNew: true,
      },
      ...this.data.form.parameters,
    ];
  }

  @Mutation
  private REMOVE_FORM_PARAMETER(tempId: number) {
    this.data.form.parameters = this.data.form.parameters.filter((parameter) => parameter.tempId !== tempId);
  }

  @Mutation
  private UPDATE_FORM_PARAMETER(payload: { tempId: number; patch: Partial<BiPanelRuleFormParameter> }) {
    this.data.form.parameters = this.data.form.parameters.map((parameter) => (
      parameter.tempId === payload.tempId ? { ...parameter, ...payload.patch } : parameter
    ));
  }

  @Mutation
  private MERGE_LEVEL_OPTION_NAMES(payload: { level: BiPanelLevelCode; names: Record<number, string> }) {
    Object.entries(payload.names).forEach(([id, name]) => {
      this.data.levelOptionNames[`${payload.level}:${id}`] = name;
    });
  }

  @Mutation
  private APPLY_LEVEL_OPTION_LABELS() {
    this.data.table.rows = this.data.table.rows.map((row) => {
      if (row.levelOptionId == null) return row;

      const cacheKey = `${row.level.code}:${row.levelOptionId}`;

      return { ...row, levelOptionLabel: this.data.levelOptionNames[cacheKey] || '' };
    });
  }
}

export default getModule(BiPanelRuleStore);
